import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { defineStore } from 'pinia'
import { ref } from 'vue'

let activeConnection: HubConnection | null = null
let pendingStart: Promise<void> | null = null

export const useSignalRStore = defineStore('signalR', () => {
  const hubConnection = ref<HubConnection | null>(null)
  const isConnected = ref(false)
  const connectionId = ref('')

  const startConnection = async () => {
    // Chamada concorrente (App.vue e setupDownload) espera o start em andamento
    // em vez de voltar sem connectionId.
    if (pendingStart && activeConnection?.state === HubConnectionState.Connecting) {
      await pendingStart.catch(() => undefined)
    }

    if (
      activeConnection?.state === HubConnectionState.Connected ||
      activeConnection?.state === HubConnectionState.Connecting
    ) {
      hubConnection.value = activeConnection
      isConnected.value = activeConnection.state === HubConnectionState.Connected
      connectionId.value = activeConnection.connectionId ?? ''
      return
    }

    if (activeConnection) {
      try {
        await activeConnection.stop()
      } catch {
        // ignore
      }
      activeConnection = null
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${process.env.VUE_APP_API_URL}/generalHub`)
      .withAutomaticReconnect([0, 1000, 5000, 10000, 30000])
      .build()

    connection.onreconnecting(() => {
      if (activeConnection !== connection) return
      isConnected.value = false
    })

    connection.onreconnected(() => {
      if (activeConnection !== connection) return
      isConnected.value = true
      connectionId.value = connection.connectionId ?? ''
    })

    connection.onclose(() => {
      if (activeConnection !== connection) return
      isConnected.value = false
      connectionId.value = ''
    })

    activeConnection = connection
    hubConnection.value = connection
    pendingStart = connection.start()

    try {
      await pendingStart
      isConnected.value = true
      connectionId.value = connection.connectionId ?? ''
    } catch (error) {
      console.error('Erro ao iniciar a conexão:', error)
      isConnected.value = false
    } finally {
      pendingStart = null
    }
  }

  const stopConnection = async () => {
    if (activeConnection) {
      try {
        await activeConnection.stop()
      } catch {
        // ignore
      }
      activeConnection = null
    }

    isConnected.value = false
    hubConnection.value = null
    connectionId.value = ''
  }

  return {
    connectionId,
    hubConnection,
    startConnection,
    stopConnection,
  }
})
